import {
  Component,
  input,
  computed,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'so-svg-icon-ui',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    @if (svgContent()) {
      <div
        [innerHTML]="customSvgContent()"
        class="so-svg-custom"
        [style.width.px]="size()"
        [style.height.px]="size()">
      </div>
    } @else if (isSvgPath()) {
      <span
        class="so-svg-mask"
        [style.width.px]="size()"
        [style.height.px]="size()"
        [style.background]="colorValue()"
        [style.mask]="maskStyle()"
        [style.webkitMask]="maskStyle()"
        [attr.title]="tooltip()"
        aria-hidden="true">
      </span>
    } @else {
      <mat-icon
        [fontSet]="fontSet()"
        [style.font-size.px]="size()"
        [style.width.px]="size()"
        [style.height.px]="size()"
        [style.color]="colorValue()"
        [attr.title]="tooltip()">
        {{ iconName() }}
      </mat-icon>
    }
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
    mat-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .so-svg-custom {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .so-svg-custom ::ng-deep svg {
      width: 100%;
      height: 100%;
    }
    .so-svg-mask {
      display: inline-flex;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoSvgIconUI {
  private readonly sanitizer = inject(DomSanitizer);

  fontSet = input<'material-icons-outlined' | 'material-icons'>('material-icons');

  // Nombre del icono (ej: 'campaign', 'mdi-campaign', 'edit', etc.)
  name = input.required<string>();

  // Alternativa: path directo a un SVG (ej: assets/img/mi-icono.svg)
  src = input<string>('');

  // Alternativa: pasar contenido SVG directamente
  svgContent = input<string>('');

  // Estilos
  color = input<string>('--so-secondary');
  size = input<number>(24);
  tooltip = input<string>('');

  // Nombre limpio para Material Icons
  protected iconName = computed(() => {
    let iconName = this.name();
    if (iconName.startsWith('mdi-')) {
      iconName = iconName.slice(4);
    }
    iconName = iconName
      .replace(/-white$/, '')
      .replace(/-primary$/, '')
      .replace(/-secondary$/, '')
      .replace(/-danger$/, '')
      .replace(/-success$/, '')
      .replace(/-warning$/, '')
      .replace(/-info$/, '')
      .replace(/-outline$/, '_outline');
    return iconName.replace(/-/g, '_');
  });

  protected colorValue = computed(() => {
    const color = this.color();
    if (!color) return 'currentColor';
    if (color.startsWith('--')) return `var(${color})`;
    if (color.startsWith('#') || color.startsWith('rgb')) return color;
    return `var(--${color})`;
  });

  protected customSvgContent = computed<SafeHtml>(() => {
    const content = this.svgContent();
    if (!content) return '';
    const coloredSvg = this.applyColorToSvg(content, this.colorValue());
    return this.sanitizer.bypassSecurityTrustHtml(coloredSvg);
  });

  private applyColorToSvg(svg: string, color: string): string {
    return svg
      .replace(/fill="[^"]*"/gi, `fill="${color}"`)
      .replace(/stroke="[^"]*"/gi, `stroke="${color}"`)
      .replace(/width="[^"]*"/gi, '')
      .replace(/height="[^"]*"/gi, '')
      .replace(/<svg/gi, '<svg style="width:100%;height:100%"');
  }

  protected isSvgPath = computed(() => {
    const directSrc = this.src();
    const iconName = this.name();
    if (directSrc) return true;
    return iconName.endsWith('.svg') || iconName.includes('/') || iconName.startsWith('assets/');
  });

  protected resolvedSrc = computed(() => {
    if (this.src()) return this.src();
    const iconName = this.name();
    if (iconName.endsWith('.svg')) {
      return iconName.includes('/') ? iconName : `assets/img/${iconName}`;
    }
    return iconName;
  });

  protected maskStyle = computed(() => `url('${this.resolvedSrc()}') no-repeat center / contain`);
}
